/**
 * <et-sfs-before-after> — draggable before/after image comparison.
 *
 * Markup contract:
 *   <et-sfs-before-after class="et-sfs-kh__media" data-start="50">
 *     <div class="et-sfs-kh__frame et-sfs-kh__frame--before"><img …></div>
 *     <div class="et-sfs-kh__frame et-sfs-kh__frame--after"><img …></div>
 *     <div class="et-sfs-kh__split">
 *       …lines…
 *       <button class="et-sfs-kh__split-handle" …></button>
 *     </div>
 *   </et-sfs-before-after>
 *
 * The split position is exposed as the --et-sfs-kh-split custom property so the
 * CSS clip-path and the handle stay in sync.
 */
if (!customElements.get('et-sfs-before-after')) {
  class EtSfsBeforeAfter extends HTMLElement {
    connectedCallback() {
      this.handle = this.querySelector('.et-sfs-kh__split-handle');
      if (!this.handle) return;

      this.position = parseFloat(this.dataset.start) || 50;
      this.dragging = false;

      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);

      this.addEventListener('pointerdown', this.onPointerDown);
      this.handle.addEventListener('keydown', this.onKeyDown);

      this.classList.add('et-sfs-kh__media--draggable');
      this.setPosition(this.position);
    }

    disconnectedCallback() {
      this.removeEventListener('pointerdown', this.onPointerDown);
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
    }

    setPosition(value) {
      this.position = Math.min(100, Math.max(0, value));
      this.style.setProperty('--et-sfs-kh-split', `${this.position}%`);
      this.handle.setAttribute('aria-valuenow', Math.round(this.position));
    }

    positionFromEvent(event) {
      const rect = this.getBoundingClientRect();
      if (!rect.width) return this.position;
      return ((event.clientX - rect.left) / rect.width) * 100;
    }

    onPointerDown(event) {
      // Ignore secondary buttons so text selection / context menus still work
      if (event.button !== undefined && event.button !== 0) return;
      this.dragging = true;
      this.setPosition(this.positionFromEvent(event));
      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
      event.preventDefault();
    }

    onPointerMove(event) {
      if (!this.dragging) return;
      this.setPosition(this.positionFromEvent(event));
    }

    onPointerUp() {
      this.dragging = false;
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
    }

    onKeyDown(event) {
      const step = event.shiftKey ? 10 : 2;
      switch (event.key) {
        case 'ArrowLeft':
          this.setPosition(this.position - step);
          break;
        case 'ArrowRight':
          this.setPosition(this.position + step);
          break;
        case 'Home':
          this.setPosition(0);
          break;
        case 'End':
          this.setPosition(100);
          break;
        default:
          return;
      }
      event.preventDefault();
    }
  }

  customElements.define('et-sfs-before-after', EtSfsBeforeAfter);
}
